<template>
    <rested-card v-if="(isSpaceArchived || isSpaceRested) && isDevelopment"></rested-card>
    <v-card flat color="transparent" v-else id="nvSnapshotOverview" class="w-100 align-self-start">
        <v-card-title class="secondary--text">Overview</v-card-title>
        <v-container fluid>
            <v-banner v-if="isInstanceObserver" class="mb-5">
                <v-avatar slot="icon" color="white" size="30"><v-icon large color="info">info</v-icon></v-avatar>
                <span class="subtitle-1">
                    You have an observer role in this space which does not allow you to work with its content. To gain access please request a viewer role.
                </span>
                <template v-slot:actions>
                    <v-btn
                        @click="submitRequest(invitationAndRequestRoleTypes.INSTANCE_ROLE, roleCodes.INSTANCE_VIEWER)"
                        target="_blank"
                        color="primary"
                        :loading="requestingRole">
                        Request role
                    </v-btn>
                </template>
            </v-banner>

            <v-alert
                v-if="schedules.length === 0 && !isSpaceArchived && isSpaceAdmin && isMasterInstance && currentSpaceType === spaceTypes.EDUCATION_SPACE"
                text
                prominent
                type="info">
                <v-row align="center">
                    <v-col class="grow">
                        <div>
                            No upcoming scheduled application starts defined. For optimal application start speed, please specify all known times when many
                            students are expected to start working on Nuvolos simultaneously.
                        </div>
                    </v-col>
                    <v-col class="shrink">
                        <v-btn
                            :to="{
                                name: 'snapshot-applications'
                            }"
                            outlined
                            small
                            color="info">
                            Go to apps for scheduling
                        </v-btn>
                    </v-col>
                </v-row>
            </v-alert>

            <v-alert v-if="isSpaceAdmin && archivalWithinThirtyDays && !isSpaceArchived" text color="info">
                <v-row align="center">
                    <v-col class="shrink"><v-icon color="info" right>info</v-icon></v-col>
                    <v-col class="grow">
                        This space is scheduled to be archived on
                        <strong>{{ spaceArchivalDate }}</strong>
                        . You can set a new archival date in Space configuration under General settings.
                    </v-col>
                </v-row>
            </v-alert>
        </v-container>
        <v-alert v-if="bundleCreated" text prominent type="info">
            <v-row align="center">
                <v-col class="grow">
                    <div class="font-weight-bold">Did you know?</div>
                    <div>You can recall distributed files easily if needed.</div>
                </v-col>
                <v-col class="shrink">
                    <v-btn href="https://docs.nuvolos.com/how-to-guides/common-workflows/object-distribution" target="_blank" outlined small color="info">
                        Learn more
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
        <v-container fluid v-if="assignmentsList.length">
            <v-row>
                <v-col v-for="(assignment, idx) in assignmentsList" xs="12" md="4" xl="3" :key="idx">
                    <TheSnapshotAssignmentTile :assignmentData="assignment" />
                </v-col>
            </v-row>
        </v-container>
        <v-container fluid>
            <v-row>
                <v-col md="8">
                    <v-card
                        style="min-height: 430px"
                        :color="readmeMdWithDefault === '' && !fetchingReadme ? 'white' : ''"
                        :disabled="isSpaceResting || isSpaceWaking">
                        <v-card-text>
                            <div v-if="fetchingReadme">
                                <v-skeleton-loader type="article@2"></v-skeleton-loader>
                                <v-skeleton-loader type="paragraph" class="ml-4"></v-skeleton-loader>
                            </div>
                            <div v-else>
                                <div style="position: absolute; top: 0; right: 0">
                                    <the-snapshot-file-editor
                                        v-if="isDevelopment && isInstanceEditor && !isSpaceRested"
                                        :isMarkdownFile="true"
                                        :isCreateReadmeFile="readmeData === null"
                                        :endpoint="`/snapshots/${$route.params.snid}/fs/files`"
                                        :fileData="readmeData || {}"
                                        language="markdown"
                                        :newReadmeFileContent="newReadmeFileContent"
                                        @readmeFileUpdated="fetchReadmeAndVideos()"
                                        @readmeFileCreated="fetchReadmeAndVideos()"></the-snapshot-file-editor>
                                </div>
                                <div v-if="failedToFetchReadme" class="d-flex align-center text-center justify-center align-center" style="min-height: 400px">
                                    <template v-if="isDevelopment">
                                        <p v-if="isInstanceArchived">
                                            This instance has been archived so there are no files in its Current State. Please
                                            <a @click="goToSnapshots()">restore</a>
                                            a snapshot to the Current State to work with files.
                                        </p>
                                        <div v-else>Failed to fetch files for this instance, please contact support.</div>
                                    </template>
                                </div>
                                <MarkdownViewer v-else-if="readmeMdWithDefault" :editorText="readmeMdWithDefault" id="overview" />
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col v-if="!showSidebarLoader" md="4">
                    <template v-if="showCheckList">
                        <TheSnapshotCourseCheckList :schedules="schedules" @mounted="courseChecklistMounted = true" />
                    </template>
                    <v-card flat v-if="videoURLs">
                        <v-divider class="my-3" />
                        <v-card-title>
                            <div class="d-flex align-center">
                                <h5 class="secondary--text">Videos</h5>
                                <v-btn color="primary" small text @click="showVideoModal">See All</v-btn>
                            </div>
                        </v-card-title>
                        <v-card-text class="pa-0">
                            <div id="nvVideoPreview">
                                <iframe
                                    :src="videoURLs[videoURLs.length - 1].url"
                                    :style="{ width: '100%', 'background-color': 'black', height: videoHeight + 'px' }"
                                    frameborder="0"
                                    allow="autoplay; fullscreen"
                                    allowfullscreen
                                    v-resize="onResize"></iframe>
                            </div>
                        </v-card-text>
                    </v-card>
                    <template v-if="!showCheckList && !isInstanceObserver">
                        <v-card flat color="transparent">
                            <v-card-title>
                                <h5 class="secondary--text">Resources</h5>
                            </v-card-title>
                            <v-card-text class="pa-0">
                                <v-list nav class="pa-0" color="transparent">
                                    <v-list-item
                                        :to="{
                                            name: 'snapshot-files',
                                            params: {
                                                oid: $route.params.oid,
                                                sid: $route.params.sid,
                                                iid: $route.params.iid,
                                                snid: $route.params.snid,
                                                fileArea: fileAreaType,
                                                localPath: currentFileLocalPath
                                            }
                                        }"
                                        :two-line="screenSize === 'large' || screenSize === 'small'"
                                        :three-line="screenSize === 'medium'"
                                        e2e-snapshot-resources-files>
                                        <v-list-item-avatar>
                                            <v-icon large>folder</v-icon>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                <h4 class="mr-2 secondary--text">Files</h4>
                                            </v-list-item-title>
                                            <v-list-item-subtitle>
                                                <span class="caption mr-3">Browse and manage files</span>
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>

                                    <v-list-item
                                        :to="{
                                            name: 'snapshot-tables',
                                            params: { oid: $route.params.oid, sid: $route.params.sid, iid: $route.params.iid, snid: $route.params.snid }
                                        }"
                                        :two-line="screenSize === 'large' || screenSize === 'small'"
                                        :three-line="screenSize === 'medium'"
                                        e2e-snapshot-resources-tables>
                                        <v-list-item-avatar>
                                            <v-icon large>mdi-table-large</v-icon>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                <h4 class="mr-2 secondary--text">Tables</h4>
                                            </v-list-item-title>
                                            <v-list-item-subtitle>
                                                <span class="caption mr-3">Query and export data</span>
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                    <v-list-item
                                        :to="{
                                            name: 'snapshot-applications',
                                            params: { oid: $route.params.oid, sid: $route.params.sid, iid: $route.params.iid, snid: $route.params.snid }
                                        }"
                                        :two-line="screenSize === 'large' || screenSize === 'small'"
                                        :three-line="screenSize === 'medium'"
                                        e2e-snapshot-resources-applications>
                                        <v-list-item-avatar>
                                            <v-icon large>desktop_windows</v-icon>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                <h4 class="mr-2 secondary--text">Applications</h4>
                                            </v-list-item-title>
                                            <v-list-item-subtitle>
                                                <span class="caption">Create and launch applications</span>
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                    <v-list-item
                                        :to="{
                                            name: 'instance-snapshots',
                                            params: { oid: $route.params.oid, sid: $route.params.sid, iid: $route.params.iid, snid: $route.params.snid }
                                        }"
                                        :two-line="screenSize === 'large' || screenSize === 'small'"
                                        :three-line="screenSize === 'medium'"
                                        e2e-snapshot-resources-snapshots>
                                        <v-list-item-avatar>
                                            <v-icon large>camera_alt</v-icon>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                <h4 class="mr-2 secondary--text">Snapshots</h4>
                                            </v-list-item-title>
                                            <v-list-item-subtitle>
                                                <span v-if="currentSpaceType === spaceTypes.EDUCATION_SPACE" class="caption">
                                                    Review timeline of saved course versions
                                                </span>
                                                <span v-else-if="currentSpaceType === spaceTypes.RESEARCH_SPACE" class="caption">
                                                    Review timeline of saved project versions
                                                </span>
                                                <span v-if="currentSpaceType === spaceTypes.VENDOR_SPACE" class="caption">
                                                    Review timeline of saved dataset versions
                                                </span>
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </v-card-text>
                        </v-card>
                    </template>
                </v-col>
                <v-col v-else>
                    <v-card flat>
                        <v-card-text>
                            <v-skeleton-loader type="article@2"></v-skeleton-loader>
                            <v-skeleton-loader type="paragraph" class="ml-4"></v-skeleton-loader>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
        <v-dialog v-model="showVideos" width="60vw" persistent>
            <v-card>
                <v-card-title class="flex-nowrap align-start">
                    Videos
                    <v-spacer></v-spacer>
                    <v-btn @click="showVideos = false" icon><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <v-carousel v-model="videoModel" v-if="videoURLs" height="55vh" hide-delimiters>
                        <v-carousel-item v-for="(video, i) in videoURLs" :key="video.url">
                            <div class="mb-2 secondary--text">{{ video.description }}</div>
                            <div class="d-flex justify-center">
                                <iframe
                                    :src="videoURLs[i].url"
                                    :style="{ width: '50vw', 'background-color': 'black', height: '50vh' }"
                                    frameborder="0"
                                    allow="autoplay; fullscreen"
                                    allowfullscreen></iframe>
                            </div>
                        </v-carousel-item>
                    </v-carousel>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { assignmentMethods } from '@/mixins/assignments'
import { queryData } from '@/mixins/query'
import { snapshotEnums } from '@/mixins/snapshot'
import { invitationsAndRequests } from '@/mixins/invitationsAndRequests'
import { readmeMdEdu, readmeMdRes, readmeMdData } from '@/modules/snapshot/readme_md.js'
import 'github-markdown-css/github-markdown.css'
import { fetchAppStartupSchedules } from '@/apis'
import { DateTime } from 'luxon'

const TheSnapshotFileEditor = () => import('../components/TheSnapshotFileEditor')
const MarkdownViewer = () => import('@/components/MarkdownViewer')
const TheSnapshotAssignmentTile = () => import('../components/TheSnapshotAssignmentTile')
const TheSnapshotCourseCheckList = () => import('../components/TheSnapshotCourseCheckList')
const RestedCard = () => import('@/modules/snapshot/components/RestedCard.vue')

export default {
    mixins: [enumsData, snapshotEnums, assignmentMethods, queryData, invitationsAndRequests],
    components: {
        TheSnapshotFileEditor,
        MarkdownViewer,
        TheSnapshotAssignmentTile,
        TheSnapshotCourseCheckList,
        RestedCard
    },
    data() {
        return {
            settingsModal: false,
            overviewTab: 0,
            readme_md_content: '',
            fetchingReadme: true,
            videoHeight: 200,
            videoURLs: null,
            showVideos: false,
            videoModel: 0,
            readmeData: null,
            failedToFetchReadme: false,
            courseChecklistMounted: false,
            fetchingVideosAndReadme: false,
            assignments: [
                {
                    long_id: 'Assignment 1',
                    bid: 1,
                    description: 'This is the description of assignment 1',
                    status: 'submitted',
                    allow_handins_until: '2021-03-02T00:07:07+00:00',
                    objects: {
                        applications: [1, 3, 4],
                        files: ['file1.txt', 'file2.txt'],
                        tables: ['table1', 'table2']
                    }
                },
                {
                    long_id: 'Assignment 2',
                    bid: 2,
                    description: 'This is the description of assignment 2',
                    status: 'tosubmit',
                    allow_handins_until: '2021-04-10T00:07:07+00:00',
                    objects: {
                        applications: [1, 3, 4],
                        files: ['file1.txt', 'file2.txt'],
                        tables: ['table1', 'table2']
                    }
                },
                {
                    long_id: 'Assignment 3',
                    bid: 3,
                    description: 'This is the description of assignment 3',
                    status: 'tosubmit',
                    allow_handins_until: '2021-03-03T00:07:07+00:00',
                    objects: {
                        applications: [1, 3, 4],
                        files: ['file1.txt', 'file2.txt'],
                        tables: ['table1', 'table2']
                    }
                }
            ],
            schedules: []
        }
    },
    created() {
        this.fetchSchedules()
    },
    computed: {
        ...mapGetters('instanceStore', ['isInstanceEditor', 'isInstanceObserver', 'isMasterInstance', 'isInstanceArchived']),
        ...mapState(['userMetadata', 'fetchingUserInfo']),
        ...mapState('instanceStore', ['instanceSnapshots']),
        ...mapGetters('spaceStore', [
            'isSpaceAdmin',
            'currentSpaceType',
            'isSpaceArchived',
            'privateInstances',
            'spaceArchivalDate',
            'isSpaceInAnyRestState',
            'isSpaceResting',
            'isSpaceRested',
            'isSpaceWaking'
        ]),
        ...mapGetters('snapshotStore', ['isDevelopment', 'currentFileLocalPath']),
        ...mapGetters('orgStore', ['isOrgManager']),
        ...mapState('orgStore', ['fetchingSpaces']),
        ...mapState('spaceStore', ['spaceBundles', 'fetchingInstances', 'fetchingSpaceBundles', 'currentSpace', 'fetchingCurrentSpace']),
        ...mapState('snapshotStore', ['fileAreaType', 'filesFetching']),
        ...mapState(['bundleCreated']),
        showSidebarLoader() {
            return (
                (!this.showCheckList || (this.showCheckList && this.courseChecklistMounted)) &&
                (this.filesFetching || this.fetchingSpaceBundles || this.fetchingInstances || this.fetchingVideosAndReadme || this.fetchingSpaces)
            )
        },
        newReadmeFileContent() {
            const content = `Add your README content here or remove it completely to restore the default information.`
            return content
        },
        showCheckList() {
            return this.currentSpaceType === this.spaceTypes.EDUCATION_SPACE && this.isMasterInstance && this.isDevelopment && this.isSpaceAdmin
        },
        instanceRearchiveAfterTimestamp() {
            return this.$store.getters['instanceStore/instanceRearchiveAfterTimestamp'](this.$route.params.iid)
        },
        assignmentsList() {
            if (this.spaceBundles.length) {
                return this.spaceBundles.filter(bundle => bundle.bundle_type_name === this.distributionReasons.ASSIGNMENT)
            }
            return []
        },
        screenSize() {
            if (this.$vuetify.breakpoint.smAndDown) {
                return 'small'
            } else if (this.$vuetify.breakpoint.mdAndDown) {
                return 'medium'
            } else if (this.$vuetify.breakpoint.lgAndup) {
                return 'large'
            }
            return 'large'
        },
        snapshotFilesystemPrefix() {
            return this.$store.getters['snapshotStore/snapshotFilesystemPrefixById'](this.$route.params.snid)
        },
        readmeMdWithDefault() {
            if (this.readme_md_content) {
                return this.readme_md_content.replace(/\[(.*?)\]\(files(.*?)\)/g, '[$1](files/files$2)')
            }
            if (this.currentSpaceType === this.spaceTypes.EDUCATION_SPACE) {
                return readmeMdEdu
            } else if (this.currentSpaceType === this.spaceTypes.RESEARCH_SPACE) {
                return readmeMdRes
            }
            return readmeMdData
        },
        archivalWithinThirtyDays() {
            const dt = DateTime.fromISO(this.spaceArchivalDate)
            const now = DateTime.now()
            return dt <= now.plus({ days: 30 })
        },
        allLoaded() {
            return Boolean(this.$route.params.snid) && !this.fetchingCurrentSpace
        }
    },
    methods: {
        queryTable(tableName) {
            this.$router.push({
                name: 'snapshot-tables',
                params: {
                    oid: this.$route.params.oid,
                    sid: this.$route.params.sid,
                    iid: this.$route.params.iid,
                    snid: this.$route.params.snid
                },
                query: { tab: this.tableViewTabNames.QUERYBUILDER, table: tableName }
            })
        },
        onResize() {
            if (document.getElementById('nvVideoPreview')) {
                this.videoHeight = Math.round(document.getElementById('nvVideoPreview').offsetWidth * 0.75)
            }
        },
        showVideoModal() {
            this.showVideos = true
        },
        goToSnapshots() {
            this.$router.push({
                name: 'instance-snapshots',
                params: {
                    oid: this.$route.params.oid,
                    sid: this.$route.params.sid,
                    iid: this.$route.params.iid,
                    snid: this.$route.params.snid
                }
            })
        },
        fetchReadmeAndVideos() {
            if (this.isSpaceArchived || this.isSpaceRested) {
                this.fetchingVideosAndReadme = false
                this.fetchingReadme = false
                return
            }

            this.fetchingVideosAndReadme = true
            const baseURL = `/snapshots/${this.$route.params.snid}/fs/files`
            this.readmeData = null
            this.fetchingReadme = true
            this.failedToFetchReadme = false
            this.$axios
                .get(baseURL)
                .then(response => {
                    let hasREADME = false
                    let hasVIDEOS = false
                    response.data.forEach(s => {
                        if (s.short_id.toLowerCase() === 'readme.md') {
                            hasREADME = true
                            this.readmeData = s
                            this.$axios
                                .get(`/files/${s.fid}/download`, {
                                    timeout: 10000,
                                    responseType: 'blob' // important
                                })
                                .then(response => {
                                    const reader = new FileReader()
                                    reader.addEventListener('loadend', e => {
                                        const text = e.target.result
                                        this.readme_md_content = text
                                    })
                                    reader.readAsText(response.data)
                                })
                                .catch(e => {
                                    console.log(e)
                                    this.readme_md_content = ''
                                })
                                .finally(() => {
                                    window.setTimeout(() => (this.fetchingReadme = false), 1000)
                                })
                        }
                        if (s.short_id.toLowerCase() === '.videos.json') {
                            hasVIDEOS = true
                            this.$axios
                                .get(`/files/${s.fid}/download`, {
                                    timeout: 10000,
                                    responseType: 'blob' // important
                                })
                                .then(response => {
                                    const reader = new FileReader()
                                    reader.addEventListener('loadend', e => {
                                        const text = e.target.result
                                        this.videoURLs = JSON.parse(text).nvVIDEOS
                                        this.$nextTick(() => {
                                            this.onResize()
                                        })
                                    })
                                    reader.readAsText(response.data)
                                })
                                .catch(e => {
                                    console.log(e)
                                })
                        }
                    })
                    if (!hasREADME) {
                        this.fetchingReadme = false
                        this.readme_md_content = ''
                    }
                    if (!hasVIDEOS) {
                        this.videoURLs = null
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data && error.response.data.code === 'item_not_found') {
                        this.failedToFetchReadme = true
                    }
                    this.fetchingReadme = false
                })
                .finally(() => {
                    this.fetchingVideosAndReadme = false
                })
        },
        async fetchSchedules() {
            const { data } = await fetchAppStartupSchedules({ sid: this.$route.params.sid })
            this.schedules = data
        }
    },
    watch: {
        allLoaded: {
            handler(canRun) {
                if (!canRun) return
                this.fetchReadmeAndVideos()
            },
            immediate: true
        }
    }
}
</script>
